import { type Spec } from "@json-render/react";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** json-render Spec 最小结构校验（root + elements[root]） */
export const isValidJsonRenderSpec = (value: unknown): value is Spec => {
  if (!isRecord(value)) {
    return false;
  }

  const { root, elements } = value;

  return (
    typeof root === "string" &&
    root.length > 0 &&
    isRecord(elements) &&
    isRecord(elements[root])
  );
};

const DYNAMIC_UI_PATTERN = /^<dynamic_ui(?:\s+[^>]*)?>([\s\S]*)<\/dynamic_ui>$/;
const DYNAMIC_UI_OPEN_TAG_PATTERN = /^<dynamic_ui([^>]*)>/;

export type DynamicUiTagAttributes = {
  collapsed?: boolean;
  id?: string;
  metrics?: string;
};

const parseBooleanAttribute = (
  value: string | undefined,
): boolean | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") {
    return true;
  }
  if (normalized === "false") {
    return false;
  }
  return undefined;
};

/**
 * 读 opening tag 上一个字符串属性。
 *
 * 两条约束都踩过坑（2026-08-13 修），改这个正则前先看清楚：
 *
 * 1. **属性名前必须有边界** `(?:^|[\s<])`。没有边界时 `id` 会命中 `data-id=` / `uuid=`
 *    并取走**别人的值** —— 不是取不到，是取错。`id` 是通知卡「曝光 ↔ 反馈」的配对主键
 *    （下游 `edgen_events` 的干扰模型靠它），取错等于静默配错。
 * 2. **单引号也要读到**。原来只取捕获组 1（双引号那支），`id='x'` 命中的是组 2，
 *    于是返回 `undefined` —— 属性明明写了却被当成缺失。
 *    现在用反向引用同一个引号字符，只留一个值捕获组。
 */
export const parseTagStringAttribute = (
  openingTag: string,
  attributeName: string,
): string | undefined => {
  // 属性名里的正则元字符要转义 —— 调用方传的是普通名字，但别让它变成注入点
  const escaped = attributeName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const attributeRegex = new RegExp(
    `(?:^|[\\s<])${escaped}\\s*=\\s*(["'])([\\s\\S]*?)\\1`,
  );
  const value = openingTag.match(attributeRegex)?.[2]?.trim();
  return value ? value : undefined;
};

/** @deprecated 内部别名，保持本文件内既有调用点不变；新代码用 [parseTagStringAttribute]。 */
const parseStringAttribute = parseTagStringAttribute;

/** 解析 `<dynamic_ui ...>` opening tag 上的 collapsed / id / metrics 属性。 */
export const parseDynamicUiTagAttributes = (
  body: string,
): DynamicUiTagAttributes => {
  const matched = body.trim().match(DYNAMIC_UI_OPEN_TAG_PATTERN);
  if (!matched?.[1]) {
    return {};
  }

  const openingTag = `<dynamic_ui${matched[1]}>`;
  const collapsed = parseBooleanAttribute(
    parseStringAttribute(openingTag, "collapsed"),
  );
  const id = parseStringAttribute(openingTag, "id");
  const metrics = parseStringAttribute(openingTag, "metrics");

  return {
    ...(collapsed === undefined ? {} : { collapsed }),
    ...(id ? { id } : {}),
    ...(metrics === undefined ? {} : { metrics }),
  };
};

/**
 * 解析整段为单个 `<dynamic_ui>...</dynamic_ui>` 包裹的 json-render Spec。
 * 用于 Tracker / Notification WS 等 payload。
 */
export const parseDynamicUiBody = (body: string): Spec | null => {
  const matched = body.trim().match(DYNAMIC_UI_PATTERN);

  if (!matched) {
    return null;
  }

  const rawJson = matched[1]?.trim();

  if (!rawJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawJson);
    return isValidJsonRenderSpec(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
